import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@material-ui/core';
import {
  Build as PartsIcon,
  ShoppingCart as PurchaseOrdersIcon,
  Assignment as WorkOrdersIcon,
  Schedule as PlanningIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { fetchDashboardStatsStart } from '../store/reducers/dashboardSlice';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stats, loading, error } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardStatsStart());
  }, [dispatch]);

  const dashboardItems = [
    {
      title: 'Parts Management',
      description: 'Manage parts, serials, and documents',
      icon: <PartsIcon fontSize="large" />,
      path: '/parts',
      color: '#1976d2',
      count: stats.totalParts,
    },
    {
      title: 'Purchase Orders',
      description: 'Create and manage purchase orders',
      icon: <PurchaseOrdersIcon fontSize="large" />,
      path: '/purchaseorders',
      color: '#388e3c',
      count: stats.totalPurchaseOrders || 0,
    },
    {
      title: 'Work Orders',
      description: 'Create and track work orders',
      icon: <WorkOrdersIcon fontSize="large" />,
      path: '/workorders',
      color: '#f57c00',
      count: stats.totalWorkOrders,
    },
    {
      title: 'Production Planning',
      description: 'Schedule and plan production',
      icon: <PlanningIcon fontSize="large" />,
      path: '/planning',
      color: '#7b1fa2',
      count: stats.activeWorkOrders,
    },
    {
      title: 'User Management',
      description: 'Manage users and permissions',
      icon: <UsersIcon fontSize="large" />,
      path: '/users',
      color: '#d32f2f',
      count: stats.totalUsers,
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'part_created':
      case 'work_order_created':
      case 'part_approved':
      case 'purchase_order_created':
        return <CheckIcon color="primary" />;
      case 'part_rejected':
      case 'purchase_order_rejected':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="action" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Operoute Dashboard
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Welcome to the React frontend for Operoute Manufacturing Resource Planning System
      </Typography>

      <Grid container spacing={3}>
        {dashboardItems.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box color={item.color} mr={2}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" component="h2">
                    {item.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {item.description}
                </Typography>
                <Typography variant="h4" color="primary">
                  {item.count || 0}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" color="primary" onClick={() => navigate(item.path)}>
                  Access Module
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} style={{ marginTop: '2rem' }}>
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: '1rem' }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {stats.recentActivity && stats.recentActivity.length > 0 ? (
              <List>
                {stats.recentActivity.slice(0, 5).map((activity) => (
                  <ListItem key={activity.id}>
                    <ListItemIcon>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.description}
                      secondary={`${activity.userName} - ${formatTimestamp(activity.timestamp)}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No recent activity
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Paper style={{ padding: '1rem' }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">Total Parts:</Typography>
                <Chip label={stats.totalParts} size="small" color="primary" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">Purchase Orders:</Typography>
                <Chip label={stats.totalPurchaseOrders || 0} size="small" color="secondary" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">Active Work Orders:</Typography>
                <Chip label={stats.activeWorkOrders} size="small" color="secondary" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">Completed Work Orders:</Typography>
                <Chip label={stats.completedWorkOrders} size="small" color="default" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">Total Users:</Typography>
                <Chip label={stats.totalUsers} size="small" color="primary" />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Total Clients:</Typography>
                <Chip label={stats.totalClients} size="small" color="secondary" />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper style={{ marginTop: '2rem', padding: '2rem' }}>
        <Typography variant="h6" gutterBottom>
          Migration Status
        </Typography>
        <Typography variant="body2" paragraph>
          This is Phase 3 of the React migration. The following features have been implemented:
        </Typography>
        <ul>
          <li>✅ Layout component with navigation</li>
          <li>✅ Parts List component with real API integration</li>
          <li>✅ Parts Edit component (ready for API integration)</li>
          <li>✅ Purchase Orders List component with real API integration</li>
          <li>✅ Purchase Orders Edit component with full form</li>
          <li>✅ Dashboard component with real statistics</li>
          <li>✅ Redux store with Redux Toolkit and Redux Saga</li>
          <li>✅ API service layer with axios</li>
          <li>✅ Authentication system (ready for integration)</li>
          <li>✅ Error handling and loading states</li>
          <li>✅ Real-time API calls to Express.js backend</li>
          <li>🔄 Purchase Orders module (complete)</li>
          <li>⏳ Work Orders module (next)</li>
          <li>⏳ Planning module (pending)</li>
          <li>⏳ User Management module (pending)</li>
        </ul>
      </Paper>
    </Box>
  );
};

export default Dashboard; 